import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { EncabezadoPagina } from '@xiltrion/ui/bloques/encabezado-pagina';
import { EstadoCargando, EstadoVacio } from '@xiltrion/ui/bloques/estados';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Icono } from '@xiltrion/ui/icono';
import { Item, ItemContent, ItemDescription, ItemTitle } from '@xiltrion/ui/item';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { apiGet } from '@/api/client';
import type { MeApp, DayApp, MeEntity, NotificationRow } from '@/api/types';
import { entry, useSession } from '@/app/session';
import { PortalApp } from './PortalApp';

const greeting = (h = new Date().getHours()) => (h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches');
const today = () => { const d = format(new Date(), "EEEE d 'de' MMMM", { locale: es }); return d[0]!.toUpperCase() + d.slice(1); };

/** Lo más urgente entre todas las apps: lo que vence primero; lo que publicó la app antes que un aviso. */
function mostUrgent(days: DayApp[]) {
  const due = (d: DayApp) => (d.urgent?.dueAt ? Date.parse(d.urgent.dueAt) : Infinity);
  return days.filter((d) => d.total && d.urgent?.link).sort((a, b) => due(a) - due(b) || (a.source === b.source ? 0 : a.source === 'APP' ? -1 : 1))[0];
}

/** Resumen de «Mi día» bajo el encabezado: cuánto espera en cuántas apps y lo más urgente. */
function DaySummary({ days, entity }: { days: DayApp[]; entity: MeEntity }) {
  const total = days.reduce((t, d) => t + d.total, 0);
  const apps = days.filter((d) => d.total).length;
  if (!total) return <>Estás al día en todas tus apps.</>;
  const top = mostUrgent(days);
  const app = top && entity.apps.find((a) => a.code === top.code);
  return (
    <>
      Te {total === 1 ? 'espera' : 'esperan'} <b className="font-semibold text-foreground tnum">{total.toLocaleString('es-CO')}</b> {total === 1 ? 'cosa' : 'cosas'} en {apps === 1 ? '1 app' : `${apps} apps`}.
      {top?.urgent && (
        <> Lo más urgente{app ? ` en ${app.name}` : ''}: <a href={top.urgent.link!} className="font-semibold text-primary underline underline-offset-2">{top.urgent.title}</a>{top.urgent.detail ? `, ${top.urgent.detail.toLowerCase()}` : ''}.</>
      )}
    </>
  );
}

/** Inicio («Mi día», GUIA §3.14): por cada app de la entidad activa, lo que espera hoy a la persona, y los últimos avisos. */
export function Home() {
  const { me, entity, superadmin } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { data: notifications = [] } = useQuery({ queryKey: ['notifications', 'home'], queryFn: () => apiGet<NotificationRow[]>('/me/notifications', { limit: 8 }) });
  // Cuelga de `notifications`: un aviso nuevo o leído (y el evento `pending`) lo refresca.
  const day = useQuery({ queryKey: ['notifications', 'day', entity?.slug], queryFn: () => apiGet<{ apps: DayApp[] }>('/me/day', { entity: entity!.slug }), enabled: !!entity?.apps.length });
  const open = (webUrl: string) => `${webUrl.replace(/\/$/, '')}/?tenant=${encodeURIComponent(entity?.slug ?? '')}`;
  // Recién ingresa: directo al inicio OIDC de la app, que con la sesión del portal entra sin mostrar su ingreso.
  const enter = (a: MeApp) => (a.loginUrl ? `${a.loginUrl}?tenant=${encodeURIComponent(entity?.slug ?? '')}` : open(a.webUrl));
  // Recién ingresa: a la app que eligió en la portada (`?app=`) si la tiene en su entidad, o a la única que tiene
  // (sin superadmin ni verificación en dos pasos pendiente). Sin salto, el `?app=` se quita de la URL.
  // `?ingreso=1`: la marca viene en la URL cuando el ingreso fue en un dominio propio y saltó al host canónico.
  const [entering] = useState(() => entry.peek() || params.get('ingreso') === '1');
  const wantedCode = params.get('app');
  const wanted = wantedCode && me && !me.mfaSetupRequired ? entity?.apps.find((a) => a.code === wantedCode) : null;
  const only = me && !superadmin && !me.mfaSetupRequired && me.entities.length === 1 && entity?.apps.length === 1 ? enter(entity.apps[0]!) : null;
  const target = entering ? (wanted ? enter(wanted) : only) : null;
  useEffect(() => {
    entry.clear();
    if (target) window.location.replace(target);
    else if ((wantedCode || params.has('ingreso')) && me) navigate('/', { replace: true });
  }, [target, wantedCode, params, me, navigate]);
  if (!me) return null;
  if (target) return <div className="p-10"><EstadoCargando /></div>;
  const byApp = new Map(day.data?.apps.map((d) => [d.code, d]));
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex items-center gap-5">
        {entity?.branding.logoUrl && <img src={entity.branding.logoUrl} alt="" className="h-20 w-auto max-w-48 shrink-0 object-contain dark:rounded-md dark:bg-white dark:px-1.5 dark:py-1" />}
        <EncabezadoPagina className="min-w-0 flex-1" arriba={<span className="font-semibold">{entity ? `${today()} · ${entity.name}` : today()}</span>} titulo={<span className="text-2xl font-semibold tracking-tight">{greeting()}, {me.displayName.split(' ')[0]}</span>} descripcion={entity?.apps.length ? (day.data ? <DaySummary days={day.data.apps} entity={entity} /> : 'Esto es lo que te espera hoy en tus apps.') : entity ? undefined : superadmin ? 'Administración de Datalog' : 'Aún no perteneces a ninguna entidad.'} acciones={entity && (entity.role === 'ADMIN' || superadmin) ? <Button variant="outline" asChild><Link to={`/entidades/${entity.slug}`}><Icono nombre="mdi:cog-outline" />Administrar entidad</Link></Button> : undefined} />
      </div>
      {!entity && superadmin ? (
        <EstadoVacio icono="mdi:shield-crown-outline" titulo="Consola de Datalog" descripcion="Como superadministrador administras las entidades y sus apps desde la consola; no necesitas pertenecer a ninguna. Para usar una app dentro de una entidad, agrégate como persona de esa entidad." accion={<Button asChild><Link to="/admin/entidades"><Icono nombre="mdi:domain" />Ir a Entidades</Link></Button>} />
      ) : !entity ? (
        <EstadoVacio titulo="Sin entidades" descripcion="Pide a tu administrador que te agregue a su entidad." />
      ) : !entity.apps.length ? (
        <EstadoVacio titulo="Sin apps" descripcion="Tu entidad no tiene apps habilitadas para ti todavía." />
      ) : (
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-label="Mi día">
          {entity.apps.map((a, i) => <PortalApp key={a.code} app={a} day={byApp.get(a.code)} failed={day.isError} href={open(a.webUrl)} index={i} />)}
        </section>
      )}
      {!!notifications.length && (
        <Card>
          <CardHeader><CardTitle className="text-base">Últimos avisos</CardTitle><CardDescription>De todas tus apps. <Link to="/avisos" className="underline">Ver todos</Link></CardDescription></CardHeader>
          <CardContent className="space-y-1">
            {notifications.map((n) => (
              <Item key={n.id} variant="muted" size="sm" asChild>
                <a href={n.link ?? '/avisos'}>
                  <Icono nombre={n.appIcon as never} className="size-5 text-muted-foreground" />
                  <ItemContent><ItemTitle>{n.title}{!n.readAt && <Badge size="sm" variant="primary-light" className="ml-2">nuevo</Badge>}</ItemTitle><ItemDescription>{n.appName} · {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true, locale: es })}{n.body ? ` · ${n.body}` : ''}</ItemDescription></ItemContent>
                </a>
              </Item>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
